'use client';

import { Box, freddyTheme, Link, ListItem } from '@coolblue-development/freddy';
import selectPropertyTrigger from '@coolblue-development/ga4-events/triggers/AlternativePrimaryItem/selectPropertyTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { PropertyExplanationModal } from '@coolblue-development/next-hemingway/client';
import SpecificationLink from '@features/product/components/Specifications/SpecificationLink';
import type { DistinctProperty } from '@features/product/typings';
import { useState } from 'react';

import { constrainedTitleStyles, stickyTitleStyles } from '../BetterOptionsBlock.styled';
import PropertyValueRow from './PropertyValueRow';

type Props = {
  displayName: string;
  values: DistinctProperty[];
  productTypeId: string | null;
  highlightBestValue: boolean;
  bestValueIndex: number;
  showDynamicHighlightsLink: boolean;
};

const PropertyRow = ({
  displayName,
  values,
  productTypeId,
  highlightBestValue,
  bestValueIndex,
  showDynamicHighlightsLink
}: Props) => {
  const { trackEvent } = useAnalytics();

  const [showPropertyExplanation, setShowPropertyExplanation] = useState<boolean>(false);

  const params = {
    property_name: displayName,
    property_value: values
      .map(property => (typeof property.value === 'string' ? property.value : ''))
      .filter(Boolean)
      .join(', ')
  };

  const hasExplanation = values[0]?.hasExplanation ?? false;

  const properties = values.map(value => ({
    displayName: value.displayName ?? '',
    hasExplanation: value.hasExplanation ?? false,
    propertyId: value.propertyId ?? '',
    value: value.value
  }));

  function togglePropertyExplanationModal(open: boolean) {
    if (!hasExplanation || open === showPropertyExplanation) return;
    setShowPropertyExplanation(open);
  }

  const highlightIndex = highlightBestValue ? bestValueIndex : 0;
  const highlightColor = highlightBestValue ? '#e0f6e066' : freddyTheme.color.background.secondary;

  return (
    <>
      <ListItem
        sx={{
          borderTop: `${freddyTheme.borderWidth[25]} solid ${freddyTheme.color.border.base}`,
          '&:last-of-type': {
            borderBottom: `${freddyTheme.borderWidth[25]} solid ${freddyTheme.color.border.base}`
          }
        }}
      >
        <Box
          py={200}
          px={300}
          sx={{
            position: 'relative',
            '&:before': {
              width: `calc(100% / ${values.length})`,
              height: '100%',
              position: 'absolute',
              left: `calc((100% / ${values.length})*${highlightIndex})`,
              top: 0,
              backgroundColor: highlightColor,
              content: '""'
            }
          }}
        >
          {showDynamicHighlightsLink &&
            (hasExplanation ? (
              <SpecificationLink
                type='button'
                component='button'
                aria-haspopup='dialog'
                onClick={() => {
                  trackEvent(selectPropertyTrigger(params));
                  togglePropertyExplanationModal(true);
                }}
                sx={{ ...stickyTitleStyles }}
              >
                {displayName}
              </SpecificationLink>
            ) : (
              <Box
                sx={{
                  ...stickyTitleStyles,
                  ...constrainedTitleStyles(values.length)
                }}
                color='additional'
              >
                {displayName}
              </Box>
            ))}

          {!showDynamicHighlightsLink &&
            (hasExplanation ? (
              <Link
                fontWeight={700}
                onClick={() => {
                  trackEvent(selectPropertyTrigger(params));
                  togglePropertyExplanationModal(true);
                }}
                sx={{
                  ...stickyTitleStyles,
                  ...constrainedTitleStyles(values.length)
                }}
              >
                {displayName}
              </Link>
            ) : (
              <Box
                sx={{
                  ...stickyTitleStyles,
                  ...constrainedTitleStyles(values.length),
                  fontWeight: 700
                }}
              >
                {displayName}
              </Box>
            ))}
        </Box>

        <PropertyValueRow
          values={values}
          highlightBestValue={highlightBestValue}
          bestValueIndex={bestValueIndex}
        />
      </ListItem>
      {hasExplanation && (
        <PropertyExplanationModal
          open={showPropertyExplanation}
          onClose={() => togglePropertyExplanationModal(false)}
          property={properties[0]}
          productTypeId={productTypeId ?? undefined}
        />
      )}
    </>
  );
};

export default PropertyRow;
